import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";
import type { Room } from "@/types/checklist";
import {
  resolveRoomsToPropertyRoomsForProperty,
  replaceChecklistRoomLinks,
  type PropertyRoomResolutionRow,
} from "@/lib/propertyChecklistRooms";

/**
 * Find or create canonical `property_rooms` for every room in the checklist, and
 * (after the checklist row exists) replace its `checklist_room_links`. Used by
 * `useCreateChecklist` / `useUpdateChecklist` so templates, onboarding flows,
 * and ad-hoc creators can never forget to seed canonical rooms.
 *
 * Skips rooms that already have `property_room_id`; we still index them in
 * `checklist_room_links` so room-usage counts stay accurate.
 */
async function ensureCanonicalRoomsForChecklist(
  propertyId: string,
  rooms: Room[]
): Promise<{ rooms: Room[]; propertyRoomIds: string[]; roomNamesOriginal: (string | null)[] }> {
  const { data: existing } = await supabase
    .from("property_rooms")
    .select("id, property_id, name, name_normalized, room_instructions, notes, archived_at, created_at")
    .eq("property_id", propertyId)
    .is("archived_at", null);
  const initialList = (existing ?? []) as PropertyRoomResolutionRow[];
  return resolveRoomsToPropertyRoomsForProperty(propertyId, rooms, initialList);
}

export interface Checklist {
  id: string;
  property_id: string;
  name: string;
  description: string | null;
  /** Optional notes for cleaners during guest-amenity staging (placement, counts). */
  guest_amenities_instructions?: string | null;
  /** Host-defined guest amenity lines (JSON array); not inventory_items. */
  guest_amenity_staging?: Json | null;
  is_default: boolean | null;
  type: string;
  rooms: unknown;
  access_verification_settings?: unknown;
  created_at: string;
  updated_at: string | null;
}

export const useChecklists = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["checklists", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("checklists")
        .select("*")
        .eq("property_id", propertyId)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as Checklist[];
    },
    enabled: !!propertyId,
    staleTime: 15_000, // Phones and other accounts change this data; recheck soon
    refetchOnMount: true,
    placeholderData: [], // Show empty array while loading
  });
};

/** Fetch a single checklist by id (e.g. for editing a PM checklist from Maintenance tab). */
export const useChecklistById = (checklistId: string | undefined) => {
  return useQuery({
    queryKey: ["checklist", checklistId],
    queryFn: async () => {
      if (!checklistId) return null;
      const { data, error } = await supabase
        .from("checklists")
        .select("*")
        .eq("id", checklistId)
        .maybeSingle();
      if (error) throw error;
      return data as Checklist | null;
    },
    enabled: !!checklistId,
    staleTime: 1000 * 60,
  });
};

export const useCreateChecklist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      name,
      type,
      description,
      rooms,
      isDefault,
    }: {
      propertyId: string;
      name: string;
      type: "cleaning" | "maintenance";
      description?: string;
      rooms: Room[];
      isDefault?: boolean;
    }) => {
      // Always resolve to canonical property_rooms so default / template-created
      // checklists don't drift from the Rooms tab (Inventory/Reports group by room).
      const {
        rooms: resolvedRooms,
        propertyRoomIds,
        roomNamesOriginal,
      } = await ensureCanonicalRoomsForChecklist(propertyId, rooms);

      const { data, error } = await supabase
        .from("checklists")
        .insert({
          property_id: propertyId,
          name,
          type: type || "cleaning",
          description: description || null,
          rooms: resolvedRooms as unknown as Json,
          is_default: isDefault || false,
          access_verification_settings: {} as Json,
        })
        .select()
        .single();

      if (error) throw error;
      if (data?.id && propertyRoomIds.length > 0) {
        try {
          await replaceChecklistRoomLinks(data.id, propertyRoomIds, roomNamesOriginal);
        } catch (linkErr) {
          // Don't roll back the checklist if linking fails; surface in console for ops.
          console.warn("[useCreateChecklist] checklist_room_links failed:", linkErr);
        }
      }
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["checklists-leave-out-staging", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property-rooms", variables.propertyId] });
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: ["checklist", data.id] });
      }
    },
  });
};

export const useUpdateChecklist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      name,
      type,
      description,
      guest_amenities_instructions,
      guest_amenity_staging,
      rooms,
      access_verification_settings,
    }: {
      id: string;
      propertyId: string;
      name: string;
      type?: "cleaning" | "maintenance";
      description?: string;
      guest_amenities_instructions?: string | null;
      guest_amenity_staging?: Json | null;
      rooms: Room[];
      access_verification_settings?: Json;
    }) => {
      // Resolve canonical property_rooms in the same pass so updates that rename
      // or add rooms keep the Rooms tab in sync.
      const {
        rooms: resolvedRooms,
        propertyRoomIds,
        roomNamesOriginal,
      } = await ensureCanonicalRoomsForChecklist(propertyId, rooms);

      const { data, error } = await supabase
        .from("checklists")
        .update({ 
          name, 
          ...(type !== undefined && { type }),
          description: description || null,
          rooms: resolvedRooms as unknown as Json,
          ...(access_verification_settings !== undefined && {
            access_verification_settings,
          }),
          ...(guest_amenities_instructions !== undefined && {
            guest_amenities_instructions: guest_amenities_instructions.trim() || null,
          }),
          ...(guest_amenity_staging !== undefined && {
            guest_amenity_staging,
          }),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      try {
        await replaceChecklistRoomLinks(id, propertyRoomIds, roomNamesOriginal);
      } catch (linkErr) {
        console.warn("[useUpdateChecklist] checklist_room_links failed:", linkErr);
      }
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["checklist", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["checklists-leave-out-staging", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property-rooms", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", variables.propertyId] });
    },
  });
};

export const useDeleteChecklist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, propertyId }: { id: string; propertyId: string }) => {
      // First, clear checklist_id from any cleaning_tasks referencing this checklist
      const { error: tasksError } = await supabase
        .from("cleaning_tasks")
        .update({ checklist_id: null })
        .eq("checklist_id", id);
      
      if (tasksError) throw tasksError;

      // Also clear from cleaning_submissions
      const { error: submissionsError } = await supabase
        .from("cleaning_submissions")
        .update({ checklist_id: null })
        .eq("checklist_id", id);
      
      if (submissionsError) throw submissionsError;

      // Also clear from checklist_rules
      const { error: rulesError } = await supabase
        .from("checklist_rules")
        .delete()
        .eq("checklist_id", id);
      
      if (rulesError) throw rulesError;

      // Now delete the checklist
      const { error } = await supabase.from("checklists").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["checklist", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["checklist-rules", variables.propertyId] });
    },
  });
};

export const useSetDefaultChecklist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      checklistId,
      propertyId,
    }: {
      checklistId: string;
      propertyId: string;
    }) => {
      // First, unset all other checklists as default for this property
      await supabase
        .from("checklists")
        .update({ is_default: false })
        .eq("property_id", propertyId);

      // Set the selected checklist as default
      const { error: checklistError } = await supabase
        .from("checklists")
        .update({ is_default: true })
        .eq("id", checklistId);

      if (checklistError) throw checklistError;

      // Update the property's default_checklist_id
      const { error: propertyError } = await supabase
        .from("properties")
        .update({ default_checklist_id: checklistId })
        .eq("id", propertyId);

      if (propertyError) throw propertyError;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property", variables.propertyId] });
    },
  });
};
